import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  SRGBColorSpace,
  Texture,
  TextureLoader,
} from "three";
import type { Bbox2 } from "../geo/bbox";
import type { SceneOrigin } from "../geo/origin";

const SWISSTOPO_WMS_URL = "https://wms.geo.admin.ch/";
const SWISSTOPO_LAYER = "ch.swisstopo.swissimage";
/** EPSG codes swisstopo's WMS accepts directly — the project's own CRS, no reprojection. */
const SUPPORTED_EPSG = new Set([2056, 21781, 3857]);
/** How far the backdrop reaches beyond the dataset's own extent, as a multiple of its size. */
const PADDING_FACTOR = 2.5;
/** Longer side of the requested image, in pixels — a sanity-check backdrop, not a base map. */
const MAX_IMAGE_SIZE = 2048;
/**
 * Vertical nudge (world units, same scale as `groundHeight`) applied below the terrain plate
 * so the backdrop sits just under it rather than exactly coplanar — on top of, not instead
 * of, the `polygonOffset` below, which only biases the depth buffer and doesn't move the
 * plane in world space.
 */
const GROUND_OFFSET = -0.005;

/**
 * A single-image swisstopo aerial photo, draped as a flat ground plane around whatever
 * dataset is loaded — replaces the plain black void outside the model's own footprint with
 * real surroundings, so a glance at the viewer is enough to tell whether the model landed
 * in the right place.
 *
 * One WMS `GetMap` request rather than a tiled pyramid: the goal is a quick visual sanity
 * check, not base-map-grade resolution, so a single image covering the padded extent is
 * enough and keeps this to one HTTP round trip.
 */
export class SwisstopoBackground {
  readonly group = new Group();
  private mesh: Mesh<BufferGeometry, MeshBasicMaterial> | null = null;
  private texture: Texture | null = null;

  constructor(private readonly origin: SceneOrigin) {
    this.group.name = "swisstopo-background";
    // Behind the terrain in the depth-buffer sense (see the material's polygonOffset in
    // `build()`), but drawn as ordinary opaque geometry — renderOrder just keeps it out of
    // the way of transparent overlays.
    this.group.renderOrder = -1;
  }

  /**
   * Builds the backdrop for `extent` (the loaded dataset's own bbox, in its CRS) at
   * `groundHeight` (a real project elevation the plane is placed just below).
   *
   * Returns false — without throwing — when the CRS isn't one swisstopo's WMS accepts, or
   * the request itself failed; either way there is simply no backdrop, which is not worth
   * interrupting the load over.
   */
  async build(extent: Bbox2, epsg: number | undefined, groundHeight: number, signal?: AbortSignal): Promise<boolean> {
    if (!epsg || !SUPPORTED_EPSG.has(epsg)) return false;

    const width = extent.maxX - extent.minX;
    const height = extent.maxY - extent.minY;
    if (!(width > 0) || !(height > 0)) return false;

    const padX = (width * (PADDING_FACTOR - 1)) / 2;
    const padY = (height * (PADDING_FACTOR - 1)) / 2;
    const padded: Bbox2 = {
      minX: extent.minX - padX,
      maxX: extent.maxX + padX,
      minY: extent.minY - padY,
      maxY: extent.maxY + padY,
    };
    const paddedWidth = padded.maxX - padded.minX;
    const paddedHeight = padded.maxY - padded.minY;
    const aspect = paddedWidth / paddedHeight;
    const outWidth = Math.max(1, Math.round(aspect >= 1 ? MAX_IMAGE_SIZE : MAX_IMAGE_SIZE * aspect));
    const outHeight = Math.max(1, Math.round(aspect >= 1 ? MAX_IMAGE_SIZE / aspect : MAX_IMAGE_SIZE));

    const params = new URLSearchParams({
      SERVICE: "WMS",
      REQUEST: "GetMap",
      VERSION: "1.1.1", // x/y (easting/northing) axis order for every CRS below, unlike 1.3.0.
      LAYERS: SWISSTOPO_LAYER,
      STYLES: "",
      FORMAT: "image/jpeg",
      SRS: `EPSG:${epsg}`,
      BBOX: `${padded.minX},${padded.minY},${padded.maxX},${padded.maxY}`,
      WIDTH: String(outWidth),
      HEIGHT: String(outHeight),
    });

    let texture: Texture;
    try {
      texture = await loadTexture(`${SWISSTOPO_WMS_URL}?${params.toString()}`, signal);
    } catch (error) {
      console.warn("[background] could not load swisstopo imagery:", error);
      return false;
    }
    if (signal?.aborted) {
      texture.dispose();
      return false;
    }

    texture.colorSpace = SRGBColorSpace;
    texture.anisotropy = 4;

    // An explicit quad rather than a rotated PlaneGeometry: positions and UVs are derived
    // the same way TerrainTile builds its own patches (U east, V north), which is the
    // convention worth matching rather than reverse-engineering PlaneGeometry's default
    // orientation after a rotation.
    // Practically the same flat elevation the terrain plate itself is drawn at (TerrainTile
    // uses this same `origin.sceneY(groundHeight)`, modulo GROUND_OFFSET below), so the two
    // never show a visible step — any gap between a georeferenced dataset and its
    // surroundings would look like the backdrop was placed wrong. The tiny GROUND_OFFSET
    // drop keeps the backdrop just under the terrain in world space; `polygonOffset` below
    // additionally biases it back in the depth buffer, in case that nudge alone isn't enough
    // to avoid z-fighting at typical camera distances.
    const y = this.origin.sceneY(groundHeight + GROUND_OFFSET);
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      "position",
      new BufferAttribute(
        new Float32Array([
          this.origin.sceneX(padded.minX), y, this.origin.sceneZ(padded.minY), // SW
          this.origin.sceneX(padded.maxX), y, this.origin.sceneZ(padded.minY), // SE
          this.origin.sceneX(padded.maxX), y, this.origin.sceneZ(padded.maxY), // NE
          this.origin.sceneX(padded.minX), y, this.origin.sceneZ(padded.maxY), // NW
        ]),
        3,
      ),
    );
    geometry.setAttribute(
      "uv",
      new BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2),
    );
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.computeBoundingSphere();

    // Double-sided: this is a single quad, so the cost of skipping backface culling is
    // negligible next to the risk of guessing the winding order wrong for one face.
    // polygonOffset (not a world-space nudge) is what keeps this from z-fighting the
    // terrain plate where the two are exactly coplanar — see the comment above.
    const material = new MeshBasicMaterial({
      map: texture,
      side: DoubleSide,
      toneMapped: false,
      polygonOffset: true,
      polygonOffsetFactor: 4,
      polygonOffsetUnits: 4,
    });
    this.mesh = new Mesh(geometry, material);
    this.texture = texture;
    this.group.add(this.mesh);
    return true;
  }

  setOpacity(opacity: number): void {
    if (!this.mesh) return;
    const material = this.mesh.material;
    material.opacity = opacity;
    material.transparent = opacity < 1;
    material.needsUpdate = true;
  }

  dispose(): void {
    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh.material.dispose();
      this.mesh = null;
    }
    this.texture?.dispose();
    this.texture = null;
  }
}

function loadTexture(url: string, signal?: AbortSignal): Promise<Texture> {
  return new Promise((resolve, reject) => {
    const loader = new TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(url, resolve, undefined, reject);
    signal?.addEventListener("abort", () => reject(new DOMException("aborted", "AbortError")), { once: true });
  });
}
